import { useAsync } from '@nxsys/hooks/useAsync';
import {
  cancelAgencyUploadedFileApi,
  getImportEmployeeByIdApi,
  getImportTimesheetByIdApi,
  importAgencyApi,
  importEmployeeApi,
  importEmployeeEmployerApi,
  importProspectApi,
  importTimesheetApi,
  importTimesheetBureauApi,
  importXMLEmployeeApi,
  saveImportAgencyByIdApi,
  saveImportEmployeeByIdApi,
  saveImportProspectByIdApi,
  saveImportTimesheetByIdApi,
  saveImportXMLEmployeeApi,
  validateImportTimesheetApi,
  validateImportXMLEmployeeApi,
} from '@nxsys/services/api/import';
import { AppState } from '@nxsys/store';
import {
  CancelAgencyUploadedFileParams,
  GetImportEmployeeByIdResponse,
  GetImportTimesheetByIdResponse,
  ImportAgencyParams,
  ImportAgencyResponse,
  ImportEmployeeParams,
  ImportEmployeeResponse,
  ImportProspectParams,
  ImportProspectResponse,
  ImportTimesheetParams,
  ImportTimesheetResponse,
  ImportXMLEmployeeParams,
  ImportXMLEmployeeResponse,
  SaveImportAgencyByIdResponse,
  SaveImportEmployeeByIdResponse,
  SaveImportProspectByIdResponse,
  SaveImportTimesheetByIdParams,
  SaveImportTimesheetByIdResponse,
  SaveImportXMLEmployeeParams,
  SaveImportXMLEmployeeResponse,
  ValidateImportTimesheetParams,
  ValidateImportTimesheetResponse,
  ValidateImportXMLEmployeeParams,
  ValidateImportXMLEmployeeResponse,
} from '@nxsys/types/api/import';
import { showMessage } from '@nxsys/utils/message';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';

export const useImportAgency = () => {
  const { value, loading, error, run } = useAsync<
    ImportAgencyResponse,
    ImportAgencyParams
  >(importAgencyApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useSaveImportAgencyById = () => {
  const { value, loading, error, run } = useAsync<
    SaveImportAgencyByIdResponse,
    string
  >(saveImportAgencyByIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetImportEmployeeById = () => {
  const { value, loading, error, run } = useAsync<
    GetImportEmployeeByIdResponse,
    string
  >(getImportEmployeeByIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useImportEmployee = () => {
  const { isBureauEmployer } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { value, loading, error, run } = useAsync<
    ImportEmployeeResponse,
    ImportEmployeeParams
  >(isBureauEmployer ? importEmployeeEmployerApi : importEmployeeApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useSaveImportEmployeeById = () => {
  const { value, loading, error, run } = useAsync<
    SaveImportEmployeeByIdResponse,
    string
  >(saveImportEmployeeByIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useImportProspect = () => {
  const { value, loading, error, run } = useAsync<
    ImportProspectResponse,
    ImportProspectParams
  >(importProspectApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useSaveImportProspectById = () => {
  const { value, loading, error, run } = useAsync<
    SaveImportProspectByIdResponse,
    string
  >(saveImportProspectByIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useImportTimesheet = () => {
  const { isBureau } = useSelector((state: AppState) => state.appStore);

  const { value, loading, error, run } = useAsync<
    ImportTimesheetResponse,
    ImportTimesheetParams
  >(isBureau ? importTimesheetBureauApi : importTimesheetApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetImportTimesheetById = () => {
  const { value, loading, error, run } = useAsync<
    GetImportTimesheetByIdResponse,
    string
  >(getImportTimesheetByIdApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useSaveImportTimesheetById = () => {
  const { value, loading, error, run } = useAsync<
    SaveImportTimesheetByIdResponse,
    SaveImportTimesheetByIdParams & { id: string }
  >(({ id, ...params }) => saveImportTimesheetByIdApi(params, id));

  const _run = async (id: string, params: SaveImportTimesheetByIdParams) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useValidateImportTimesheet = () => {
  const { value, loading, error, run } = useAsync<
    ValidateImportTimesheetResponse,
    ValidateImportTimesheetParams
  >(validateImportTimesheetApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useCancelAgencyUploadedFileById = () => {
  const { value, loading, error, run } = useAsync<
    { success: boolean },
    CancelAgencyUploadedFileParams & { id: number }
  >(({ id, ...params }) => cancelAgencyUploadedFileApi(id, params));

  const _run = async (id: number, params: CancelAgencyUploadedFileParams) => {
    return run({ id, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useImportXMLEmployee = () => {
  const { value, loading, error, run } = useAsync<
    ImportXMLEmployeeResponse,
    ImportXMLEmployeeParams
  >(importXMLEmployeeApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useValidateImportXMLEmployee = () => {
  const { value, loading, error, run } = useAsync<
    ValidateImportXMLEmployeeResponse,
    ValidateImportXMLEmployeeParams
  >(validateImportXMLEmployeeApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useSaveImportXMLEmployee = () => {
  const { value, loading, error, run } = useAsync<
    SaveImportXMLEmployeeResponse,
    SaveImportXMLEmployeeParams
  >(saveImportXMLEmployeeApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};
